// homeWidgetCatalog.js — THE HOME WIDGET REGISTRY: every house widget the
// workspace "/" board can show, and the ones it shows before anybody
// personalises it (DESIGN-WL-HOME-DEFAULTS-ALL-WORKSPACES-1).
//
// ONE registry, three readers:
//   • the board (pages/main.js `HOME_CATALOG`, `_DEFAULT_VISIBLE_WIDGETS`,
//     `homeCatalogFor`) — what it renders and what ships on;
//   • /admin/dashboard-defaults and the /admin/module Panels tab — the rows an
//     admin can turn on for the whole workspace (`homePanelRows` below).
// Both admin pages used to carry their own eleven-row `HOME_PANELS` copy, so
// every widget born after it (Next up, the App strip shortcuts, the team
// pulse / priority mix / workload summaries, the mail inbox) was on the board
// and missing from the pages that set the board's defaults.
//
// FRANK: every export is self-contained (FA204) — the lists live inside the
// functions that return them, and siblings are reached through `this.call`.

// Catalog of every house widget that lives on the home page. Keys match the
// `spec.id` passed into `_widget(...)` in pages/main.js; titles + icons feed
// the customize drawer when a widget is hidden so it can be restored, and the
// admin panel pages' rows (`homePanelRows`).
//
// `subtitle` (optional) is the SAME literal bundle key the widget's own card
// header uses, repeated here because the drawer needs it and two catalog
// rows can carry the SAME title: `assets` and `app-documents` both resolve
// to "Notes", `broadcast` and `app-broadcast` both to "Broadcast" (measured
// 2026-09-10). It is written out per row, never derived from the title key —
// `__tests__/i18nWorkspaceExtraction.test.js` requires every polyglot key to
// be a LITERAL, because a key built by concatenation is invisible to the
// extractor and to the "does this key exist" check. A row whose CARD header
// shows a live count (`assets`: "3 notes", `broadcast`: "2 unread") still
// carries one here: the drawer lists a widget that is OFF the board, so there
// is no count to show and nothing to tell it apart from its app-shortcut
// namesake. Those rows name a STATIC `workspace.widget.<id>.subtitle` key that
// exists for the drawer alone, alongside the card's own `subtitle<Count|Empty|
// Loading>` family.
//
// Keep this in sync with the widget declarations in pages/main.js
// (MainColumn.GridContainer) — adding a widget without listing it here means
// the drawer can't surface it after removal, and no admin can turn it on.
export const homeWidgetCatalog = function homeWidgetCatalog() {
  return [
    // `place` names a row that renders in the page FRAME, not as a board card:
    // 'head' — the page head (the greeting and its summary); 'dock' — an App
    // strip shortcut. An app-mode workspace keeps its frame defaults and stands
    // down only the board cards (functions/homeDefaultWidgets.js
    // houseDefaultHiddenMap; Nika 2026-10-03, "Greeting + house dock").
    { id: 'greeting', title: 'workspace.catalog.greeting', icon: 'user', place: 'head' },
    // Module widgets (GreenIG's coverbanner/policies/claims/…) are NOT
    // catalogued here — homeCatalogFor appends the ACTIVE module's declared
    // widgets from the manifest stash (_allModuleWidgetsFor).
    { id: 'received', title: 'workspace.catalog.received', icon: 'check' },
    { id: 'given', title: 'workspace.catalog.given', icon: 'check' },
    // S8 (NIKA BATCH 2026-08-16) — workspace-wide ticket-status summary,
    // board-status.py's visual properties (proportional bar, fixed-width
    // counts, a who-can-act legend), not personal like Received/Given above.
    { id: 'teampulse', title: 'workspace.catalog.teampulse', icon: 'analytics' },
    // S8 second pass — the same summary language on the other two axes a
    // board is read on: how urgent the open work is, and who carries it.
    { id: 'prioritymix', title: 'workspace.catalog.prioritymix', icon: 'analytics' },
    { id: 'workload', title: 'workspace.catalog.workload', icon: 'people' },
    { id: 'projects', title: 'workspace.catalog.projects', icon: 'layers' },
    {
      id: 'assets',
      title: 'workspace.catalog.assets',
      icon: 'document',
      subtitle: 'workspace.widget.assets.subtitle'
    },
    {
      id: 'agents',
      title: 'workspace.catalog.agents',
      icon: 'star',
      subtitle: 'workspace.widget.agents.subtitle'
    },
    { id: 'youragents', title: 'workspace.catalog.youragents', icon: 'star' },
    {
      id: 'analytics',
      title: 'workspace.catalog.analytics',
      icon: 'analytics',
      subtitle: 'workspace.widget.analytics.subtitle'
    },
    { id: 'events', title: 'workspace.catalog.events', icon: 'date outline' },
    { id: 'next-up', title: 'workspace.catalog.nextUp', icon: 'date outline' },
    {
      id: 'activity',
      title: 'workspace.catalog.activity',
      icon: 'analytics',
      subtitle: 'workspace.widget.activity.subtitle'
    },
    {
      id: 'teamtime',
      title: 'workspace.catalog.teamtime',
      icon: 'globe',
      subtitle: 'workspace.widget.teamtime.subtitle'
    },
    {
      id: 'broadcast',
      title: 'workspace.catalog.broadcast',
      icon: 'bell',
      subtitle: 'workspace.widget.broadcast.subtitle'
    },
    // The built-in mail client's inbox card (architecture/MAIL.md §3.6).
    // `gateRoute` is the FIRST house row to carry one — see homeCatalogFor: mail
    // is behind a feature flag AND the app-access ACL, so a workspace with mail
    // off must not be OFFERED the widget in the customize drawer either.
    {
      id: 'mail-inbox',
      title: 'workspace.catalog.mailInbox',
      icon: 'email',
      gateRoute: '/mail'
    },
    // App-shortcut icon widgets — default on a fresh workspace (see the
    // AppStudioWidget… declarations in GridContainer + _DEFAULT_VISIBLE_WIDGETS).
    {
      id: 'app-studio',
      place: 'dock',
      title: 'workspace.catalog.appStudio',
      icon: 'layers',
      subtitle: 'workspace.widget.appStudio.subtitle'
    },
    {
      id: 'app-infra',
      place: 'dock',
      title: 'workspace.catalog.appInfra',
      icon: 'server',
      subtitle: 'workspace.widget.appInfra.subtitle'
    },
    {
      id: 'app-tickets',
      place: 'dock',
      title: 'workspace.catalog.appTickets',
      icon: 'check',
      subtitle: 'workspace.widget.appTickets.subtitle'
    },
    {
      id: 'app-chat',
      place: 'dock',
      title: 'workspace.catalog.appChat',
      icon: 'chat',
      subtitle: 'workspace.widget.appChat.subtitle'
    },
    {
      id: 'app-calendar',
      place: 'dock',
      title: 'workspace.catalog.appCalendar',
      icon: 'date outline',
      subtitle: 'workspace.widget.appCalendar.subtitle'
    },
    { id: 'app-reminders', title: 'workspace.catalog.appReminders', icon: 'clockOutline', place: 'dock' },
    {
      id: 'app-meet',
      place: 'dock',
      title: 'workspace.catalog.appMeet',
      icon: 'video',
      subtitle: 'workspace.widget.appMeet.subtitle'
    },
    {
      id: 'app-broadcast',
      place: 'dock',
      title: 'workspace.catalog.appBroadcast',
      icon: 'bell',
      subtitle: 'workspace.widget.appBroadcast.subtitle'
    },
    {
      id: 'app-documents',
      place: 'dock',
      title: 'workspace.catalog.appDocuments',
      icon: 'document',
      subtitle: 'workspace.widget.appDocuments.subtitle'
    },
    {
      id: 'app-agents',
      place: 'dock',
      title: 'workspace.catalog.appAgents',
      icon: 'star',
      subtitle: 'workspace.widget.appAgents.subtitle'
    },
    {
      id: 'app-team',
      place: 'dock',
      title: 'workspace.catalog.appTeam',
      icon: 'people',
      subtitle: 'workspace.widget.appTeam.subtitle'
    },
    {
      id: 'app-analytics',
      place: 'dock',
      title: 'workspace.catalog.appAnalytics',
      icon: 'analytics',
      subtitle: 'workspace.widget.appAnalytics.subtitle'
    },
    {
      id: 'app-people',
      place: 'dock',
      title: 'workspace.catalog.appPeople',
      icon: 'user',
      subtitle: 'workspace.widget.appPeople.subtitle'
    },
    {
      id: 'app-health',
      place: 'dock',
      title: 'workspace.catalog.appHealth',
      icon: 'eye',
      subtitle: 'workspace.widget.appHealth.subtitle'
    }
  ]
}

// The widgets a fresh home shows; every other catalog row is opt-in via the
// Customize drawer, and pages/main.js derives `_DEFAULT_HIDDEN_WIDGETS` as the
// catalog minus this list — so a widget added to the catalog is hidden by
// default automatically; add its id here to ship it on by default instead.
// History: greeting-only on 2026-06-16 (Nika "by default it should show only
// GreetingWidget"), the app shortcuts from 2026-07-19, Next up from
// 2026-09-27 — each id was born into this list, never flipped into it.
export const homeDefaultVisibleWidgets = function homeDefaultVisibleWidgets() {
  return [
    'greeting',
    // Next up — the day's next event, on by default (Nika's pick "Next up
    // widget on", HOME_AMBIENT_SKIN_SPEC.md §0).
    'next-up',
    // Cool-app icon shortcuts — on by default so a fresh workspace isn't bare.
    'app-studio',
    'app-tickets',
    'app-chat',
    'app-calendar',
    'app-reminders',
    'app-meet',
    'app-broadcast',
    'app-documents',
    'app-agents',
    'app-team',
    'app-analytics',
    'app-people',
    'app-health'
  ]
}

// The admin panel pages' rows — /admin/dashboard-defaults and the
// /admin/module Panels tab: EVERY widget the home can show, read off this
// registry (the board's own list), never a copy.
//   • the house rows whose route gate is on — the same `navItemVisible` gate
//     the board's `homeCatalogFor` applies — minus the greeting (the page
//     head, always shown);
//   • every declared widget of the active takeover module and of the
//     installed apps (`workspaceProject.widgets`, then each
//     `workspaceApps[].widgets`; the first listed wins an id).
// Each row carries `state`, read with the precedence the board applies to a
// panel set (functions/homeDefaultWidgets.js `panelSetHiddenMap`):
//   'on'       the panel set names it — on for every member by default;
//   'default'  a house default (Next up, an App strip shortcut) on a workspace
//              whose panel set does not govern the house defaults: the board
//              shows it whatever the set says, so the page shows it on and its
//              switch cannot turn it off. A takeover module's set (Nika's call
//              pending) keeps these rows switchable; app mode locks only the
//              frame rows (the house Dock apps — Nika 2026-10-03, answer A) and
//              keeps its board cards switchable;
//   'off'      the set can turn it on.
// Module and app rows read 'on' / 'off' exactly as before.
// `this` is the calling element — siblings through this.call (FA204).
export const homePanelRows = function homePanelRows(root) {
  const el = this
  const call = (name, ...args) =>
    el && typeof el.call === 'function' ? el.call(name, ...args) : undefined
  const r = root && typeof root === 'object' ? root : {}
  const bag = r.workspaceSettings && typeof r.workspaceSettings === 'object' ? r.workspaceSettings : {}
  const panels = Array.isArray(bag.home_default_panels) ? bag.home_default_panels : []
  const catalog = call('homeWidgetCatalog')
  const defaults = call('homeDefaultVisibleWidgets')
  const houseDefaults = new Set(Array.isArray(defaults) ? defaults : [])
  // A takeover module's set governs every row. App mode locks only the frame
  // rows (catalog `place`: the house Dock apps), which show there like on every
  // workspace (Nika 2026-10-03, "Greeting + house dock"); its board cards stay
  // switchable. A general workspace locks every house default.
  const takeover = !!call('takeoverModuleRef', r)
  const appMode = !takeover && !!call('isAppModeWorkspace', r)
  const locked = (w) => !takeover && houseDefaults.has(w.id) && (!appMode || !!w.place)
  const rows = []
  const seen = new Set(['greeting'])
  for (const w of Array.isArray(catalog) ? catalog : []) {
    if (!w || !w.id || seen.has(w.id)) continue
    if (w.gateRoute && call('navItemVisible', r, w.gateRoute) === false) continue
    seen.add(w.id)
    const named = panels.includes(w.id)
    const state = named ? 'on' : locked(w) ? 'default' : 'off'
    rows.push({ id: w.id, label: w.title || w.id, state })
  }
  const planes = [r.workspaceProject && r.workspaceProject.widgets]
  for (const app of Array.isArray(r.workspaceApps) ? r.workspaceApps : []) {
    planes.push(app && app.widgets)
  }
  for (const widgets of planes) {
    for (const w of Array.isArray(widgets) ? widgets : []) {
      if (!w || !w.id || seen.has(w.id)) continue
      seen.add(w.id)
      rows.push({ id: w.id, label: w.title || w.id, state: panels.includes(w.id) ? 'on' : 'off' })
    }
  }
  return rows
}
